// src/components/DepartmentAndMapManager.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api"; // APIエンドポイントのパスをプロジェクトに合わせて調整してください
import OrgChart from "./OrgChart"; // 既存の組織図コンポーネントを保持
import "./DepartmentMasters.css"; // 既存のCSSを活用

// --- DepartmentMastersのヘルパー関数 ---
const formatCode = (n) => String(n).padStart(4, "0");
const isParentEligible = (code) => {
  const c = String(code || "");
  return /00$/.test(c) || c === "0001" || c === "0002";
};

export default function DepartmentAndMapManager() {
  const navigate = useNavigate();
  
  // 画面モードの切り替え管理 ('chart' = 組織図画面, 'master' = 部署マスター画面)
  const [viewMode, setViewMode] = useState("chart");

  // 部署データの共通ステータス管理
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ departmentId: "", departmentName: "", parent_id: "" });
  const [draftRow, setDraftRow] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  
  // 新しい部署が追加された際にOrgChartを再レンダリングするためのキー
  const [refreshChartKey, setRefreshChartKey] = useState(0); 

  // 部署ツリーデータをリスト表示用にフラット化
  const flattenDepts = (list, flat = [], seen = new Set()) => {
    list.forEach(d => {
      if (!seen.has(d.id)) {
        seen.add(d.id);
        flat.push(d);
      }
      if (d.children && d.children.length > 0) flattenDepts(d.children, flat, seen);
    });
    return flat;
  };

  const fetchDepartments = async () => {
    try {
      const res = await api.get("/api/departments/tree/");
      const data = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
      const flatData = flattenDepts(data);
      {/*}
      //自動に新しい部署・課を別の色表示、
      if (departments.length > 0 && flatData.length > departments.length) {
        //部署・課数を増える場合　→　新しい部署・課を作成
        const oldIds = new Set(departments.map(d => String(d.id)));
        const newDept = flatData.find(d => !oldIds.has(String(d.id)));

        if (newDept) {
          console.log("新しい部署・課を作成しました:", newDept.id);
          setHighlightId(String(newDept.id)); 
        }
      } else if (editingId) {
        setHighlightId(String(editingId));
      }
      */}
      setDepartments(flatData);
      // 右側の組織図を自動的に更新
      setRefreshChartKey(prev => prev + 1);
    } catch (err) {
      console.error("エラーが発生しました。:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const parentOptions = departments.filter(d => isParentEligible(d.departmentId));

  const nextTopCode = () => {
    const tops = departments.filter(d => !d.parent_id);
    const max = tops.reduce((m, d) => Math.max(m, parseInt(d.departmentId, 10) || 0), 0);
    return formatCode(max ? max + 100 : 100);
  };

  const nextChildCode = (parentDept) => {
    const children = departments.filter(d => d.parent_id === parentDept.id);
    const parentNum = parseInt(parentDept.departmentId, 10) || 0;
    const max = children.reduce((m, d) => Math.max(m, parseInt(d.departmentId, 10) || 0), parentNum);
    return formatCode(max + 1);
  };

  // --- 部署作成 of ビジネスロジック ---
  const handleStartAddTop = () => {
    setEditingId(null);
    setDraftRow({ departmentId: nextTopCode(), departmentName: "", parent_id: "" });
  };

  const handleStartAddChild = (dept) => {
    setEditingId(null);
    setDraftRow({ departmentId: nextChildCode(dept), departmentName: "", parent_id: dept.id });
  };

  const handleSaveDraft = async () => {
    if (!draftRow) return;
    if (!draftRow.departmentId?.trim() || !draftRow.departmentName?.trim()) {
      alert("部署コードと部署名を入力してください。");
      return;
    }
    try {
      {/*}
      await api.post("/api/departments/", {
        departmentId: draftRow.departmentId,
        departmentName: draftRow.departmentName,
        parent: draftRow.parent_id || null,
      });
      */}
      const payload = {
        departmentId: draftRow.departmentId,
        departmentName: draftRow.departmentName,
        parent: draftRow.parent_id || null,
      }
      const res = await api.post("/api/departments/", payload);
      if (draftRow.departmentId) {
        setHighlightId(String(draftRow.departmentId).trim());  
      } else if (res.data && res.data.departmentId) {
        setHighlightId(String(res.data.departmentId).trim());
      }

      setDraftRow(null);
      fetchDepartments(); 
    } catch (err) {
      alert("追加時にエラーが発生しました。コードが重複していないか確認してください。");
    }
  };

  const startEdit = (dept) => {
    setDraftRow(null);
    setEditingId(dept.id);
    setEditData({
      departmentId: dept.departmentId || "",
      departmentName: dept.departmentName || "",
      parent_id: dept.parent_id || "",
    });
  };

  const handleUpdate = async (id) => {
    try {
      await api.patch(`/api/departments/${id}/`, {
        departmentId: editData.departmentId,
        departmentName: editData.departmentName,
        parent: editData.parent_id || null,
      });
      setEditingId(null);
      fetchDepartments();
    } catch (err) {
      alert("更新時にエラーが発生しました。");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("この部署を削除してもよろしいですか？")) return;
    try {
      await api.delete(`/api/departments/${id}/`);
      fetchDepartments();
    } catch (err) {
      alert("エラーが発生しました。");
    }
  };

  // --- 【共通】セレクトボックスコンポーネント ---
  const GlobalParentSelect = ({ value, onChange, excludeId, styleOverrides = {} }) => (
    <select 
      style={{ fontSize: "12px", padding: "4px 6px", borderRadius: "4px", border: "1px solid #cbd5e1", ...styleOverrides }} 
      value={value} 
      onChange={onChange}
    >
      <option value="">-- 最上位 --</option>
      {parentOptions
        .filter(d => d.id !== excludeId)
        .map(d => (
          <option key={d.id} value={d.id}>
            {d.departmentId}:{d.departmentName}
          </option>
        ))}
    </select>
  );

  // --- 1. 左側リスト用のインライン・レンダリング（会社組織図タブ用） ---
  const renderLeftTreeRows = (parentId = null, level = 0) => {
    return departments
      .filter(d => (parentId === null ? !d.parent_id : d.parent_id === parentId))
      .sort((a, b) => (a.departmentId || "").localeCompare(b.departmentId || "", undefined, { numeric: true }))
      .map(dept => {
        const isEditing = editingId === dept.id;
        const hasChildPermission = isParentEligible(dept.departmentId);

        return (
          <React.Fragment key={dept.id}>
            <tr style={{ fontSize: "13px" }}>
              <td style={{ textAlign: "left", paddingLeft: `${level * 16 + 4}px`, paddingTop: "8px", paddingBottom: "8px" }}>
                
                {isEditing ? (
                  // 編集フォームエリア：入力欄を大きくし、保存・キャンセルボタンを確実に下の行へ配置
                  <div style={{ background: "#eff6ff", padding: "10px", borderRadius: "6px", border: "1px solid #2563eb", marginTop: "4px" }}>
                    <div style={{ color: "#2563eb", fontSize: "11px", fontWeight: "bold", marginBottom: "6px" }}>📝 部署情報の編集</div>
                    <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                      <input 
                        style={{ width: "65px", fontSize: "13px", padding: "6px", height: "32px", borderRadius: "4px", border: "1px solid #cbd5e1" }} 
                        value={editData.departmentId} 
                        onChange={e => setEditData({ ...editData, departmentId: e.target.value })} 
                        placeholder="コード"
                      />
                      <input 
                        style={{ flexGrow: 1, fontSize: "13px", padding: "6px", height: "32px", borderRadius: "4px", border: "1px solid #cbd5e1" }} 
                        value={editData.departmentName} 
                        onChange={e => setEditData({ ...editData, departmentName: e.target.value })} 
                        placeholder="部署名"
                      />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <GlobalParentSelect 
                        value={editData.parent_id} 
                        excludeId={dept.id} 
                        onChange={e => setEditData({ ...editData, parent_id: e.target.value })} 
                        styleOverrides={{ width: "100%", height: "32px" }}
                      />
                    </div>
                    
                    <div style={{ display: "flex", gap: "16px", fontSize: "12px", borderTop: "1px solid #dbeafe", paddingTop: "6px" }}>
                      <span style={{ color: "#16a34a", cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }} onClick={() => handleUpdate(dept.id)}>保存</span>
                      <span style={{ color: "#475569", cursor: "pointer", textDecoration: "underline" }} onClick={() => setEditingId(null)}>キャンセル</span>
                    </div>
                  </div>
                ) : (
                  // 通常表示モード (左右に綺麗に分離)
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {level > 0 && <span style={{ color: "#94a3b8" }}></span>}
                      <span><strong>[{dept.departmentId}]</strong> {dept.departmentName}</span>
                    </span>
                    
                    {/* 右側の固定等幅ボタンエリア */}
                    <div style={{ display: "flex", gap: "8px", fontSize: "12px", whiteSpace: "nowrap", flexShrink: 0, paddingRight: "4px" }}>
                      <div style={{ width: "50px", textAlign: "left" }}>
                        {hasChildPermission ? (
                          <span style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline" }} onClick={() => handleStartAddChild(dept)}>+子部署</span>
                        ) : (
                          <span style={{ visibility: "hidden" }}>+子部署</span>
                        )}
                      </div>
                      <div style={{ width: "30px", textAlign: "center" }}>
                        <span style={{ color: "#475569", cursor: "pointer", textDecoration: "underline" }} onClick={() => startEdit(dept)}>編集</span>
                      </div>
                      <div style={{ width: "30px", textAlign: "center" }}>
                        <span style={{ color: "#dc2626", cursor: "pointer", textDecoration: "underline" }} onClick={() => handleDelete(dept.id)}>削除</span>
                      </div>
                    </div>
                  </div>
                )}
              </td>
            </tr>
            
            {/* +子部署の入力フォーム */}
            {draftRow && draftRow.parent_id === dept.id && (
              <tr style={{ background: "#fef08a", fontSize: "13px" }}>
                <td style={{ textAlign: "left", paddingLeft: `${(level + 1) * 16 + 4}px`, paddingTop: "6px", paddingBottom: "6px" }}>
                  <div style={{ padding: "10px", borderRadius: "6px", border: "1px solid #16a34a", marginTop: "2px" }}>
                    <div style={{ color: "#16a34a", fontSize: "11px", fontWeight: "bold", marginBottom: "6px" }}>子部署の新規追加</div>
                    <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                      <input 
                        style={{ width: "65px", fontSize: "13px", padding: "6px", height: "32px", borderRadius: "4px", border: "1px solid #cbd5e1" }} 
                        value={draftRow.departmentId} 
                        onChange={e => setDraftRow({ ...draftRow, departmentId: e.target.value })} 
                        placeholder="コード" 
                      />
                      <input 
                        style={{ flexGrow: 1, fontSize: "13px", padding: "6px", height: "32px", borderRadius: "4px", border: "1px solid #cbd5e1" }} 
                        value={draftRow.departmentName} 
                        onChange={e => setDraftRow({ ...draftRow, departmentName: e.target.value })} 
                        placeholder="子部署名" 
                        autoFocus 
                      />
                    </div>
                    <div style={{ display: "flex", gap: "16px", borderTop: "1px solid #fef08a", paddingTop: "6px" }}>
                      <span style={{ color: "#16a34a", cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }} onClick={handleSaveDraft}>登録</span>
                      <span style={{ color: "#475569", cursor: "pointer", textDecoration: "underline" }} onClick={() => setDraftRow(null)}>キャンセル</span>
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {/* 子部署ツリーの再帰描画 */}
            {renderLeftTreeRows(dept.id, level + 1)}
          </React.Fragment>
        );
      });
  };

  // --- 2. 部署マスター画面用のテーブル行レンダリング (フルサイズ) ---
  const renderMasterTreeRows = (parentId = null, level = 0) => {
    const rows = departments
      .filter(d => (parentId === null ? !d.parent_id : d.parent_id === parentId))
      .sort((a, b) => (a.departmentId || "").localeCompare(b.departmentId || "", undefined, { numeric: true }))
      .map(dept => {
        const isSelectedMasterEditing = editingId === dept.id;
        const hasChildPermission = isParentEligible(dept.departmentId);

        return (
          <React.Fragment key={dept.id}>
            <tr className={isSelectedMasterEditing ? "row-editing" : ""}>
              <td className="dept-name-cell" style={{ paddingLeft: `${level * 25 + 12}px` }}>
                {level > 0 && <span className="tree-branch"></span>}
                {isSelectedMasterEditing ? (
                  <input
                    className="edit-input"
                    value={editData.departmentId}
                    onChange={e => setEditData({ ...editData, departmentId: e.target.value })}
                  />
                ) : (
                  <strong>{dept.departmentId}</strong>
                )}
              </td>
              <td>
                {isSelectedMasterEditing ? (
                  <input
                    className="edit-input"
                    value={editData.departmentName}
                    onChange={e => setEditData({ ...editData, departmentName: e.target.value })}
                  />
                ) : (
                  dept.departmentName
                )}
              </td>
              <td>
                {isSelectedMasterEditing ? (
                  <GlobalParentSelect
                    value={editData.parent_id}
                    excludeId={dept.id}
                    onChange={e => setEditData({ ...editData, parent_id: e.target.value })}
                    styleOverrides={{ width: "100%", height: "auto", className: "edit-input" }}
                  />
                ) : (
                  <span className="badge-parent">
                    {departments.find(d => d.id === dept.parent_id)?.departmentName || "なし"}
                  </span>
                )}
              </td>
              
              {/* 部署マスタータブの操作ボタン列を完全均等配置 */}
              <td style={{ textAlign: "center" }}>
                {isSelectedMasterEditing ? (
                  <div style={{ display: "inline-flex", gap: "8px" }}>
                    <button className="btn-save" onClick={() => handleUpdate(dept.id)}>保存</button>
                    <button className="btn-cancel" onClick={() => setEditingId(null)}>キャンセル</button>
                  </div>
                ) : (
                  <div style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
                    <div style={{ width: "95px", textAlign: "left" }}>
                      {hasChildPermission ? (
                        <button className="btn-add-child" style={{ margin: 0 }} onClick={() => handleStartAddChild(dept)}>子部署追加</button>
                      ) : (
                        <span style={{ display: "inline-block", width: "95px" }}></span>
                      )}
                    </div>
                    <button className="btn-edit" onClick={() => startEdit(dept)}>編集</button>
                    <button className="btn-delete" onClick={() => handleDelete(dept.id)}>削除</button>
                  </div>
                )}
              </td>
            </tr>
            {renderMasterTreeRows(dept.id, level + 1)}
            {draftRow && draftRow.parent_id === dept.id && renderMasterDraftRow(level + 1)}
          </React.Fragment>
        );
      });

    if (parentId === null && draftRow && !draftRow.parent_id) {
      rows.push(renderMasterDraftRow(0));
    }
    return rows;
  };

  const renderMasterDraftRow = (level) => (
    <tr className="row-editing" key="__draft__">
      <td className="dept-name-cell" style={{ paddingLeft: `${level * 25 + 12}px` }}>
        {level > 0 && <span className="tree-branch"></span>}
        <input
          className="edit-input"
          value={draftRow.departmentId}
          onChange={e => setDraftRow({ ...draftRow, departmentId: e.target.value })}
          placeholder="部署コード"
        />
      </td>
      <td>
        <input
          className="edit-input"
          value={draftRow.departmentName}
          onChange={e => setDraftRow({ ...draftRow, departmentName: e.target.value })}
          placeholder="部署名"
          autoFocus
        />
      </td>
      <td>
        <GlobalParentSelect
          value={draftRow.parent_id}
          onChange={e => setDraftRow({ ...draftRow, parent_id: e.target.value })}
          styleOverrides={{ width: "100%", height: "auto", className: "edit-input" }}
        />
      </td>
      <td style={{ textAlign: "center" }}>
        <button className="btn-save" onClick={handleSaveDraft}>登録</button>
        <button className="btn-cancel" onClick={() => setDraftRow(null)}>キャンセル</button>
      </td>
    </tr>
  );

  if (loading) return <div className="dept-loading">読み込み中...</div>;

  return (
    <div className="dept-container" style={{ maxWidth: "100%", padding: "10px 20px" }}>
      {/* ページのメインヘッダー */}
      <header className="dept-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
        {/* 左側のタブボタン */}
        <div style={{ display: "flex", gap: "8px", zIndex: 2 }}>
          {/*}
          <button 
            className={`dept-tab-btn ${viewMode === "chart" ? "active-tab" : ""}`}
            style={{ padding: "6px 16px", fontWeight: "bold", cursor: "pointer", background: viewMode === "chart" ? "#2563eb" : "#f1f5f9", color: viewMode === "chart" ? "#fff" : "#1e293b", border: "1px solid #cbd5e1", borderRadius: "6px" }}
            onClick={() => { setViewMode("chart"); setEditingId(null); setDraftRow(null); }}
          >
            会社組織図
          </button>
          <button 
            className={`dept-tab-btn ${viewMode === "master" ? "active-tab" : ""}`}
            style={{ padding: "6px 16px", fontWeight: "bold", cursor: "pointer", background: viewMode === "master" ? "#2563eb" : "#f1f5f9", color: viewMode === "master" ? "#fff" : "#1e293b", border: "1px solid #cbd5e1", borderRadius: "6px" }}
            onClick={() => { setViewMode("master"); setEditingId(null); setDraftRow(null); }}
          >
            部署マスター
          </button>
          */}
        </div>

        {/* 中央に配置されたタイトル */}
        <h1 style={{ fontSize: "22px", margin: 0, position: "absolute", left: "50%", transform: "translateX(-50%)", width: "auto", textAlign: "center", zIndex: 1 }}>
           組織・部署管理図
        </h1>

        {/* 右側の戻るボタン */}
        <button className="dept-back-btn" style={{ zIndex: 2 }} onClick={() => navigate(-1)}>戻る</button>
      </header>

      {/* ===== 画面モード1: 組織図 (2カラム分割レイアウト) ===== */}
      {viewMode === "chart" && (
        <div className="main-split-layout" style={{ display: "flex", gap: "20px", marginTop: "15px" }}>
          
          {/* 左側: 部署リスト */}
          <div className="left-panel-create" style={{ width: "380px", minWidth: "380px", flexShrink: 0, background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "15px", color: "#1e293b", textAlign: "left" }}>🏢 組織構成</h3>
              <button className="dept-add-btn" style={{ padding: "4px 10px", fontSize: "12px" }} onClick={handleStartAddTop}>追加</button>
            </div>

            <table className="dept-table" style={{ width: "100%", background: "#ffffff" }}>
              <tbody>
                {draftRow && !draftRow.parent_id && (
                  <tr style={{ background: "#fef08a" }}>
                    <td style={{ padding: "10px", textAlign: "left" }}>
                      <div style={{ color: "#16a34a", fontSize: "11px", fontWeight: "bold", marginBottom: "6px" }}>➕ 最上位部署の追加</div>
                      <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                        <input style={{ width: "65px", fontSize: "13px", padding: "6px", height: "32px", borderRadius: "4px", border: "1px solid #2563eb" }} value={draftRow.departmentId} onChange={e => setDraftRow({ ...draftRow, departmentId: e.target.value })} placeholder="コード" />
                        <input style={{ flexGrow: 1, fontSize: "13px", padding: "6px", height: "32px", borderRadius: "4px", border: "1px solid #2563eb" }} value={draftRow.departmentName} onChange={e => setDraftRow({ ...draftRow, departmentName: e.target.value })} placeholder="部署名" autoFocus />
                      </div>
                      <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
                        <span style={{ color: "#16a34a", cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }} onClick={handleSaveDraft}>登録</span>
                        <span style={{ color: "#475569", cursor: "pointer", textDecoration: "underline" }} onClick={() => setDraftRow(null)}>キャンセル</span>
                      </div>
                    </td>
                  </tr>
                )}
                {/* 昇順・インライン対応のツリー表示 */}
                {renderLeftTreeRows()}
              </tbody>
            </table>
          </div>

          {/* 右側: 組織図 */}
          <div className="right-panel-map" style={{ flexGrow: 1, background: "#ffffff", padding: "5px", borderRadius: "8px", border: "1px solid #cbd5e1", overflow: "hidden" }}>
            <OrgChart key={refreshChartKey} highlightId={highlightId} clearHighlight={() => setHighlightId(null)}/>
          </div>

        </div>
      )}

      {/* ===== 画面モード2: 部署マスター (フルサイズ管理テーブル) ===== */}
      {viewMode === "master" && (
        <div className="create-view-section" style={{ marginTop: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
            <p className="dept-hint" style={{ margin: 0 }}>
              「追加」で最上位部署を、各部署の「子部署追加」でその配下に子部署を登録できます。
            </p>
            <button className="dept-add-btn" onClick={handleStartAddTop}>✚ 追加</button>
          </div>

          <div className="dept-table-wrap">
            <table className="dept-table">
              <thead>
                <tr>
                  <th>部署コード</th>
                  <th>部署名</th>
                  <th>上位部署</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {renderMasterTreeRows()}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}